'use client';

import { c as _c } from "react/compiler-runtime";
import { cn } from '@morpho-org/ui-v6/utils';
import { Children, type ComponentProps, forwardRef, type JSX, type ReactElement } from 'react';
import { InternalLink } from '@/components/common/InternalLink';
import { TableVariantContext, useTableVariant } from '../Table/Table';

/**
 * Shared table container: 0.5px border + 20px radius + light-only dp4 shadow.
 * Two-level like the old Radix ScrollArea (outer clips + sizes; inner scrolls):
 * the outer `overflow-hidden` keeps the wide table's intrinsic width from leaking
 * into the shrink-to-fit page layout (which would push the tables under the sticky
 * sidebar), while the inner `overflow-auto` does the scrolling — styled by
 * @repo/tailwind-config's global scrollbar rule.
 */
export const TableScrollArea = forwardRef<HTMLDivElement, ComponentProps<'div'>>((t0, ref) => {
  const $ = _c(13);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("overflow-hidden border-[0.5px] border-border rounded-[20px] shadow-md dark:shadow-none", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== children) {
    t2 = <div className="size-full min-w-0 overflow-auto">{children}</div>;
    $[6] = children;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== props || $[9] !== ref || $[10] !== t1 || $[11] !== t2) {
    t3 = <div ref={ref} className={t1} {...props}>{t2}</div>;
    $[8] = props;
    $[9] = ref;
    $[10] = t1;
    $[11] = t2;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  return t3;
});
TableScrollArea.displayName = 'TableScrollArea';
export const Table = t0 => {
  const $ = _c(14);
  let children;
  let className;
  let props;
  let tableVariant;
  if ($[0] !== t0) {
    ({
      children,
      tableVariant,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = tableVariant;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    tableVariant = $[4];
  }
  let t1;
  if ($[5] !== className) {
    t1 = cn("w-full border-separate border-spacing-0 tabular-nums", className);
    $[5] = className;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== children || $[8] !== props || $[9] !== t1) {
    t2 = <table className={t1} {...props}>{children}</table>;
    $[7] = children;
    $[8] = props;
    $[9] = t1;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] !== t2 || $[12] !== tableVariant) {
    t3 = <TableVariantContext.Provider value={tableVariant}>{t2}</TableVariantContext.Provider>;
    $[11] = t2;
    $[12] = tableVariant;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  return t3;
};
export const Thead = t0 => {
  const $ = _c(11);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  const tableVariant = useTableVariant();
  const t1 = tableVariant === "compact-filter" && "bg-card";
  let t2;
  if ($[4] !== className || $[5] !== t1) {
    t2 = cn(t1, className);
    $[4] = className;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== children || $[8] !== props || $[9] !== t2) {
    t3 = <thead className={t2} {...props}>{children}</thead>;
    $[7] = children;
    $[8] = props;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
};
export const Th = t0 => {
  const $ = _c(13);
  let children;
  let className;
  let props;
  let tableVariant;
  if ($[0] !== t0) {
    ({
      children,
      tableVariant,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = tableVariant;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    tableVariant = $[4];
  }
  const t1 = tableVariant === "compact-filter" ? "py-xs px-md bg-transparent" : "bg-card pr-[72px] pl-md py-[10px]";
  const t2 = tableVariant !== "compact-filter" && "[&:first-of-type]:rounded-tl-[20px] [&:last-of-type]:rounded-tr-[20px]";
  let t3;
  if ($[5] !== className || $[6] !== t1 || $[7] !== t2) {
    t3 = cn("text-3xs text-secondary-foreground h-[36px] font-normal whitespace-nowrap text-start", "border-b-[0.5px] border-border", t1, t2, className);
    $[5] = className;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== children || $[10] !== props || $[11] !== t3) {
    t4 = <th className={t3} {...props}>{children}</th>;
    $[9] = children;
    $[10] = props;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
};
export const Tbody = t0 => {
  const $ = _c(10);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    const {
      children: t1,
      className: t2,
      tableVariant: _tableVariant,
      ...t3
    } = t0;
    children = t1;
    className = t2;
    props = t3;
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("[&_tr:last-child_td:first-of-type]:rounded-bl-[20px] [&_tr:last-child_td:last-of-type]:rounded-br-[20px]", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== children || $[7] !== props || $[8] !== t1) {
    t2 = <tbody className={t1} {...props}>{children}</tbody>;
    $[6] = children;
    $[7] = props;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
};
export const Tr = t0 => {
  const $ = _c(9);
  let children;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = props;
  } else {
    children = $[1];
    props = $[2];
  }
  const isAnchorRow = (Children.toArray(children)[0] as ReactElement<{
    href?: string;
  } | null>).props?.href;
  if (isAnchorRow) {
    let t1;
    if ($[3] !== children || $[4] !== props) {
      t1 = <tr className="[&:hover>td>a>div]:bg-accent relative whitespace-nowrap" {...props}>{children}</tr>;
      $[3] = children;
      $[4] = props;
      $[5] = t1;
    } else {
      t1 = $[5];
    }
    return t1;
  }
  let t1;
  if ($[6] !== children || $[7] !== props) {
    t1 = <tr {...props}>{children}</tr>;
    $[6] = children;
    $[7] = props;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  return t1;
};
export const Td = t0 => {
  const $ = _c(28);
  let children;
  let className;
  let href;
  let onNavigate;
  let prefetch;
  let props;
  let style;
  let t1;
  let width;
  if ($[0] !== t0) {
    ({
      children,
      tableVariant: t1,
      width,
      href,
      prefetch,
      onNavigate,
      className,
      style,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = href;
    $[4] = onNavigate;
    $[5] = prefetch;
    $[6] = props;
    $[7] = style;
    $[8] = t1;
    $[9] = width;
  } else {
    children = $[1];
    className = $[2];
    href = $[3];
    onNavigate = $[4];
    prefetch = $[5];
    props = $[6];
    style = $[7];
    t1 = $[8];
    width = $[9];
  }
  const tableVariant = t1 === undefined ? "compact" : t1;
  const t2 = tableVariant === "compact-filter" ? "py-xs pr-[40px] pl-md" : "py-xs pr-[72px] pl-md";
  let t3;
  if ($[10] !== t2) {
    t3 = cn("bg-card box-border flex h-[44px] items-center whitespace-nowrap content-center", "border-b-[0.5px] border-border [tr:last-child_&]:border-b-0", t2, "td-content");
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== children || $[13] !== t3) {
    t4 = <div className={t3}>{children}</div>;
    $[12] = children;
    $[13] = t3;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const content = t4;
  const t5 = width || "auto";
  let t6;
  if ($[15] !== style || $[16] !== t5) {
    t6 = {
      width: t5,
      ...style
    };
    $[15] = style;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== content || $[19] !== href || $[20] !== onNavigate || $[21] !== prefetch) {
    t7 = href ? <InternalLink href={href} prefetch={prefetch} onClick={onNavigate}>{content}</InternalLink> : content;
    $[18] = content;
    $[19] = href;
    $[20] = onNavigate;
    $[21] = prefetch;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== className || $[24] !== props || $[25] !== t6 || $[26] !== t7) {
    t8 = <td className={className} style={t6} {...props}>{t7}</td>;
    $[23] = className;
    $[24] = props;
    $[25] = t6;
    $[26] = t7;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  return t8;
};